import React from "react";
import tableStyles from "../../styles/Table.module.css";
import TextLink from "../TextLink";
import DownloadButton from "../DownloadButton";

type Props = {
    apr: number;
    annualFee: number;
};

export default function InterestTable(props: Props) {
    return (
        <>
            <div className={tableStyles.title}>Important Information</div>
            <div className={tableStyles.inputContainer}>
                <DownloadButton
                    url={`${process.env.REACT_APP_BASE_PATH}/Interest_Rates_2026.pdf`}
                />
            </div>
            <div className={tableStyles.header}>Interest Rates and Interest Charges</div>
            <div className={tableStyles.labelValueContainer}>
                <div className={tableStyles.labelValueRow}>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.label}>
                            Annual Percentage Rate (APR) for Purchases
                        </div>
                        <div className={tableStyles.value}>
                            <div>
                                <span className={tableStyles.valueBoldImportant}>0%</span>{" "}
                                Introductory APR for one year. After that, your APR will be
                                <span className={tableStyles.valueBoldImportant}>
                                    {" "}
                                    {props.apr}%
                                </span>
                                .
                            </div>
                            <div>This APR will vary with the market based on the Prime Rate.</div>
                        </div>
                    </div>
                </div>
                <div className={tableStyles.labelValueRow}>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.label}>
                            APR for Cash Advances and Balance Transfers
                        </div>
                        <div className={tableStyles.value}>
                            <div>
                                <span className={tableStyles.valueBold}>{props.apr}%</span> This APR
                                will vary with the market based on the Prime Rate.
                            </div>
                        </div>
                    </div>
                </div>
                <div className={tableStyles.labelValueRow}>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.label}>
                            How to Avoid Paying Interest on Purchases
                        </div>
                        <div className={tableStyles.value}>
                            <div>
                                Your due date is at least 21 days after the close of each billing
                                cycle. We will not charge you any interest on purchases if you pay
                                the entire balance by your due date each month.
                            </div>
                        </div>
                    </div>
                </div>
                <div className={tableStyles.labelValueRow}>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.label}>Minimum Interest Charge</div>
                        <div className={tableStyles.value}>
                            <div>
                                If you are charged interest, the charge will be no less than $.50
                                (fifty cents).
                            </div>
                        </div>
                    </div>
                </div>
                <div className={tableStyles.labelValueRow}>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.label}>
                            For Credit Card Tips from the Consumer Financial Protection Bureau
                        </div>
                        <div className={tableStyles.value}>
                            <div>
                                To learn more about factors to consider when applying for or using a
                                credit card, visit the website of the Consumer Financial Protection
                                Bureau at www.consumerfinance.gov/learnmore
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={tableStyles.header}>Fees</div>
            <div className={tableStyles.labelValueContainer}>
                <div className={tableStyles.labelValueRow}>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.label}>Annual Fee</div>
                        <div className={tableStyles.value}>
                            <div>
                                No Annual Fee for one year, then
                                <span className={tableStyles.valueBold}> $25 </span>
                                if your credit limit is less than{" "}
                                <span className={tableStyles.valueBold}>$1,000</span> and
                                <span className={tableStyles.valueBold}> ${props.annualFee} </span>
                                if your credit limit is equal to or greater than{" "}
                                <span className={tableStyles.valueBold}>$1,000.</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={tableStyles.labelValueRow}>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.label}>Transaction Fees</div>
                        <div className={tableStyles.value} />
                    </div>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.labelDetail}>Cash Advance</div>
                        <div className={tableStyles.value}>
                            <div>
                                Either <span className={tableStyles.valueBold}>$3</span> or
                                <span className={tableStyles.valueBold}> 3%</span> of the amount of
                                each cash advance, whichever is greater.
                            </div>
                        </div>
                    </div>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.labelDetail}>Balance Transfer*</div>
                        <div className={tableStyles.value}>
                            <div>
                                Either <span className={tableStyles.valueBold}>$3</span> or
                                <span className={tableStyles.valueBold}> 3%</span> of the amount of
                                each transfer, whichever is greater.
                            </div>
                        </div>
                    </div>
                </div>
                <div className={tableStyles.labelValueRow}>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.label}>Penalty Fees</div>
                        <div className={tableStyles.value} />
                    </div>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.labelDetail}>Late Payment</div>
                        <div className={tableStyles.value}>
                            <div>
                                Up to <span className={tableStyles.valueBold}>$41.</span>
                            </div>
                        </div>
                    </div>
                    <div className={tableStyles.labelValue}>
                        <div className={tableStyles.labelDetail}>Returned Payment</div>
                        <div className={tableStyles.value}>
                            <div>
                                Up to <span className={tableStyles.valueBold}>$41.</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={tableStyles.header}>How We Will Calculate Your Balance:</div>
            <div className={tableStyles.body}>
                We use a method called “average daily balance (including new purchases).”
            </div>
            <div className={tableStyles.fineprint}>
                *Balances transferred from another credit card account up to your available credit
                limit are treated as a cash advance.
            </div>
            <div className={tableStyles.fineprint}>
                If you have any questions, call Customer Service toll-free:{" "}
                <span className={tableStyles.valueBold}>1-800-733-1732</span>.
            </div>
            <div className={tableStyles.fineprint}>
                Note: In determining if you qualify for an account, we may use the information on
                the Credit Card Request Form plus information about you from other sources,
                including credit reports. By responding to this credit card offer, you authorize the
                Bank to obtain credit reports about you, both now and in the future, for any
                legitimate business purpose associated with the account or request for an account,
                including but not limited to reviewing, modifying, renewing or collection on your
                account.
            </div>
            <div className={tableStyles.notice}>
                Patriot Act Notice: Federal Law requires all financial institutions to obtain,
                verify and record information that identifies each person who opens an account,
                including your name, address, date of birth and other information that will allow us
                to verify your identity.
            </div>
            <div className={tableStyles.fineprint}>
                <span className={tableStyles.valueBold}>
                    MILITARY LENDING ACT TOLL-FREE NUMBER:{" "}
                </span>
                For a statement of the Military Annual Percentage Rate or MAPR that is applicable to
                covered borrowers and a description of a covered borrower&#39;s payment obligation,
                please call, toll-free,{" "}
                <span className={tableStyles.valueBold}>1-844-435-1814</span>.
            </div>
            <div className={tableStyles.fineprint}>
                <span className={tableStyles.valueBold}>CALIFORNIA RESIDENTS: </span>A married
                applicant may apply for a separate account.
            </div>
            <div className={tableStyles.fineprint}>
                <span className={tableStyles.valueBold}>NEW YORK RESIDENTS:</span> Consumer reports
                may be requested in connection with the processing of this application and any
                resulting account. Upon your request, we will inform you of the names and addresses
                of any consumer reporting agencies which have provided us with such reports. New
                York residents may contact the New York State Department of Financial Services to
                obtain a comparative list of credit card rates, fees and grace periods:{" "}
                <span className={tableStyles.valueBold}>1-877-226-5697</span> or
                <TextLink
                    className={tableStyles.link}
                    text=" www.dfs.ny.gov"
                    href="https://www.dfs.ny.gov"
                />
            </div>
            <div className={tableStyles.fineprint}>
                <span className={tableStyles.valueBold}>OHIO RESIDENTS:</span> The Ohio law against
                discrimination requires that all creditors make credit equally available to all
                creditworthy customers, and that credit reporting agencies maintain separate credit
                histories on each individual upon request. The Ohio Civil Rights Commission
                administers compliance with this law.
            </div>
            <div className={tableStyles.fineprint}>
                <span className={tableStyles.valueBold}>WISCONSIN RESIDENTS:</span> No agreement,
                court order, or individual statement applying to marital property will adversely
                affect a creditor’s interests unless prior to the time credit is granted, the
                creditor is furnished with a copy of the agreement, court order or statement, or has
                actual knowledge of the adverse provision.
            </div>
            <div className={tableStyles.fineprint}>
                By submitting a request for a credit card, you acknowledge and agree that 1) you
                must be at least 18 (or if you are 18 and your residence is in Alabama or Nebraska,
                you are of sound mind or are not a ward of the state, respectively); 2) your Credit
                Card Request Form must include your Yearly Income, which is the total amount you
                receive or reasonably expect to receive each year, including money from Employment,
                and money received from Interest, Dividends, Benefits, Public Assistance, etc.
                indicating that you have an independent ability to make the required minimum
                payments each month; 3) all information you provide is accurate and complete; 4)
                your initial credit limit may be as low as $150, and you can reduce your credit
                limit even further to as little as $100 -- or you can request an increase at any
                time subject to Bank approval. The Bank will also periodically review your credit
                limit and may increase or decrease it at any time and promptly mail you a written
                notice of such change. Note, too, that you can always &quot;freeze&quot; your credit
                limit at any time to a specified level to prevent any increase, or you can just
                allow the Bank to increase your credit limit automatically, as you demonstrate your
                ability to handle more credit; 5) your card protects you with Zero Liability for
                Unauthorized Use -- which means that &quot;if you didn&#39;t buy it, you don&#39;t
                pay for it&quot;; 6) when you use your card, it is to your advantage to carefully
                review your monthly billing statements, and decide, each month, whether to pay your
                balance in full, or spread your payments over several months, or just pay the
                &quot;Minimum Payment&quot; necessary to keep your account in good standing by
                always making your payments in a timely manner and by never exceeding your credit
                limit, to start building a good credit history.
            </div>
        </>
    );
}
